import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPatient } from '../../services/patientService';
import { getCase, getCasePhases } from '../../services/caseService';
import { getPhasePhotos } from '../../services/photoService';
import {
  saveSmileDesign,
  updateSmileDesign,
  getSmileDesign,
  getSmileDesigns,
} from '../../services/smileDesignService';
import { logAudit, AUDIT_ACTIONS } from '../../services/auditService';
import { useSmileDesignerHistory } from '../../hooks/useSmileDesignerHistory';
import { getToothShape } from '../../utils/toothShapes';
import { DEFAULT_DRAW_SETTINGS, hexToRgba } from '../../utils/drawingUtils';
import * as fabric from 'fabric';
import SmileDesignerToolbar from './SmileDesignerToolbar';
import SmileDesignerCanvas, { fitBackgroundImage } from './SmileDesignerCanvas';
import SmileDesignerSidebar from './SmileDesignerSidebar';
import { generateAiSmileDesign } from '../../services/aiDesignService';

const SmileDesigner = () => {
  const { patientId, caseId } = useParams();
  const { userProfile } = useAuth();

  // Data state
  const [patient, setPatient] = useState(null);
  const [treatmentCase, setTreatmentCase] = useState(null);
  const [phases, setPhases] = useState([]);
  const [selectedPhase, setSelectedPhase] = useState('');
  const [photos, setPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Design state
  const [activeMode, setActiveMode] = useState('overlay');
  const [activeTool, setActiveTool] = useState(null);
  const [fabricCanvas, setFabricCanvas] = useState(null);
  const [placingPoint, setPlacingPoint] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedDesignId, setSelectedDesignId] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const [referencePoints, setReferencePoints] = useState({
    midline: null,
    leftCommissure: null,
    rightCommissure: null,
    incisalEdge: null,
  });

  const [designParams, setDesignParams] = useState({
    showTeeth: false,
    preset: 'golden',
    toothVariant: 'natural',
    centralWidth: 0.15,
    lateralRatio: 0.618,
    canineRatio: 0.382,
    smileArcCurvature: 0.03,
    toothColor: '#F0EDE6',
    toothOpacity: 0.75,
    teethPerSide: 5,
    // Shape tool defaults (used by canvas)
    shapeFill: 'transparent',
    shapeStroke: '#000000',
    shapeStrokeWidth: 2,
    fontSize: 16,
    fontColor: '#000000',
  });

  const [drawSettings, setDrawSettings] = useState(DEFAULT_DRAW_SETTINGS);
  const fileInputRef = useRef(null);

  // AI Design state
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [generationsRemaining, setGenerationsRemaining] = useState(null);
  const [originalPhotoURL, setOriginalPhotoURL] = useState(null);

  // Undo/Redo
  const { saveState, undo, redo, canUndo, canRedo, clearHistory } = useSmileDesignerHistory(fabricCanvas);

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      if (!userProfile) return;
      try {
        setLoading(true);
        const [patientData, caseData, phasesData] = await Promise.all([
          getPatient(userProfile.practiceId, userProfile.id, patientId),
          getCase(userProfile.practiceId, userProfile.id, patientId, caseId),
          getCasePhases(userProfile.practiceId, patientId, caseId),
        ]);
        setPatient(patientData);
        setTreatmentCase(caseData);
        setPhases(phasesData);

        // Auto-select first phase with photos
        const phaseWithPhotos = phasesData.find(p => p.photos && p.photos.length > 0);
        if (phaseWithPhotos) {
          setSelectedPhase(phaseWithPhotos.id);
        }

        // Load saved designs
        const designsData = await getSmileDesigns(userProfile.practiceId, patientId, caseId);
        setDesigns(designsData);
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [patientId, caseId, userProfile]);

  // Load photos when phase changes
  useEffect(() => {
    const loadPhotos = async () => {
      if (!userProfile || !selectedPhase) {
        setPhotos([]);
        return;
      }
      try {
        const photosData = await getPhasePhotos(userProfile.practiceId, patientId, caseId, selectedPhase);
        setPhotos(photosData);
        // Auto-select frontal_smile or first photo
        const frontalSmile = photosData.find(p => p.category === 'frontal_smile');
        setSelectedPhoto(frontalSmile || photosData[0] || null);
      } catch (err) {
        console.error('Error loading photos:', err);
      }
    };
    loadPhotos();
  }, [selectedPhase, userProfile, patientId, caseId]);

  // Update brush settings when tool/settings change
  useEffect(() => {
    if (!fabricCanvas) return;

    if (activeMode === 'mockup') {
      if (activeTool === 'brush') {
        const brush = new fabric.PencilBrush(fabricCanvas);
        brush.color = hexToRgba(drawSettings.brushColor, drawSettings.brushOpacity);
        brush.width = drawSettings.brushWidth;
        fabricCanvas.freeDrawingBrush = brush;
        fabricCanvas.isDrawingMode = true;
      } else if (activeTool === 'eraser') {
        const brush = new fabric.PencilBrush(fabricCanvas);
        brush.color = '#1a1a2e'; // match background
        brush.width = drawSettings.eraserWidth;
        fabricCanvas.freeDrawingBrush = brush;
        fabricCanvas.isDrawingMode = true;
      } else if (activeTool === 'whitening') {
        const brush = new fabric.PencilBrush(fabricCanvas);
        brush.color = `rgba(255, 255, 255, ${drawSettings.whiteningOpacity})`;
        brush.width = drawSettings.whiteningWidth;
        fabricCanvas.freeDrawingBrush = brush;
        fabricCanvas.isDrawingMode = true;
      } else {
        fabricCanvas.isDrawingMode = false;
      }
    } else {
      fabricCanvas.isDrawingMode = false;
    }
  }, [fabricCanvas, activeMode, activeTool, drawSettings]);

  // Handle direct photo upload from local device
  const handleUploadPhoto = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const blobUrl = URL.createObjectURL(file);
    setSelectedPhoto({
      id: `upload-${Date.now()}`,
      category: file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' '),
      downloadURL: blobUrl,
      isLocalUpload: true,
    });
  }, []);

  const triggerFileUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't capture if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      // Don't capture if Fabric.js IText is being edited
      if (fabricCanvas) {
        const active = fabricCanvas.getActiveObject();
        if (active && active.isEditing) return;
      }

      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z' && !e.shiftKey) {
          e.preventDefault();
          undo();
        } else if ((e.key === 'z' && e.shiftKey) || e.key === 'y') {
          e.preventDefault();
          redo();
        } else if (e.key === 's') {
          e.preventDefault();
          handleSave();
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!fabricCanvas) return;
        const active = fabricCanvas.getActiveObjects();
        if (active.length > 0) {
          e.preventDefault();
          active.forEach(o => fabricCanvas.remove(o));
          fabricCanvas.discardActiveObject();
          fabricCanvas.renderAll();
          saveState();
        }
      } else if (e.key === 'Escape') {
        setPlacingPoint(null);
        if (fabricCanvas) {
          fabricCanvas.discardActiveObject();
          fabricCanvas.renderAll();
        }
      } else if (activeMode === 'mockup') {
        // Tool shortcuts
        if (e.key === 'b') setActiveTool('brush');
        else if (e.key === 'e') setActiveTool('eraser');
        else if (e.key === 't') setActiveTool('text');
        else if (e.key === 'w') setActiveTool('whitening');
        else if (e.key === 'r') setActiveTool('rectangle');
      } else {
        // Mode shortcuts
        if (e.key === '1') setActiveMode('overlay');
        else if (e.key === '2') setActiveMode('mockup');
        else if (e.key === '3') setActiveMode('template');
        else if (e.key === '4') setActiveMode('ai');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricCanvas, activeMode, undo, redo, saveState]);

  // Reference point handlers
  const handleReferencePointChange = useCallback((key, value) => {
    setReferencePoints(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleClearPoint = useCallback((key) => {
    setReferencePoints(prev => ({ ...prev, [key]: null }));
  }, []);

  const handleStartPlacing = useCallback((key) => {
    setPlacingPoint(key);
  }, []);

  const handlePlacingPointDone = useCallback(() => {
    setPlacingPoint(null);
    saveState();
  }, [saveState]);

  // Design preset
  const handleApplyPreset = useCallback((preset) => {
    setDesignParams(prev => ({
      ...prev,
      ...preset.params,
      preset: preset.id,
      showTeeth: true,
    }));
  }, []);

  // Regenerate teeth
  const handleRegenerateTeeth = useCallback(() => {
    if (!fabricCanvas) return;
    // Remove existing teeth and let the canvas effect re-render
    const teeth = fabricCanvas.getObjects().filter(o => o.customType === 'tooth');
    teeth.forEach(o => fabricCanvas.remove(o));
    fabricCanvas.renderAll();

    // Force re-render by toggling showTeeth
    setDesignParams(prev => ({ ...prev, showTeeth: false }));
    setTimeout(() => {
      setDesignParams(prev => ({ ...prev, showTeeth: true }));
      saveState();
    }, 50);
  }, [fabricCanvas, saveState]);

  // Apply template
  const handleApplyTemplate = useCallback((template) => {
    if (!fabricCanvas) return;

    // Remove existing teeth and groups
    const existing = fabricCanvas.getObjects().filter(o =>
      o.customType === 'tooth' || o.customType === 'toothGroup'
    );
    existing.forEach(o => fabricCanvas.remove(o));

    const canvasW = fabricCanvas.getWidth();
    const canvasH = fabricCanvas.getHeight();

    const centerX = referencePoints.midline ? referencePoints.midline.x : canvasW / 2;
    const incisalY = referencePoints.incisalEdge ? referencePoints.incisalEdge.y : canvasH * 0.55;
    const smileWidth = (referencePoints.leftCommissure && referencePoints.rightCommissure)
      ? Math.abs(referencePoints.rightCommissure.x - referencePoints.leftCommissure.x)
      : canvasW * 0.5;

    const centralW = smileWidth * 0.15;
    const toothHeight = centralW * 1.2;
    const color = template.defaultColor;
    const toothPaths = [];

    [-1, 1].forEach(side => {
      let xOffset = 0;

      template.teeth.forEach((toothDef, i) => {
        const shape = getToothShape(toothDef.type, toothDef.variant);
        if (!shape) return;

        const w = centralW * toothDef.relativeWidth;
        const h = toothHeight * toothDef.relativeHeight;
        const arcOffset = template.archCurvature * i * i * 15;

        const toothX = centerX + side * (xOffset + w / 2);
        const toothY = incisalY + arcOffset;

        const path = new fabric.Path(shape.path, {
          left: toothX,
          top: toothY,
          originX: 'center',
          originY: 'top',
          fill: color,
          opacity: 0.75,
          stroke: 'rgba(255,255,255,0.9)',
          strokeWidth: 1.5,
          strokeLineJoin: 'round',
          strokeLineCap: 'round',
          customType: 'tooth',
          toothType: toothDef.type,
          toothVariant: toothDef.variant,
          side: side === -1 ? 'left' : 'right',
          toothIndex: i,
        });

        // Scale using Fabric's actual path dimensions (not nominal shape.width)
        const scaleX = w / path.width;
        const scaleY = h / path.height;
        path.set({
          scaleX: side === -1 ? -scaleX : scaleX,
          scaleY: scaleY,
        });

        toothPaths.push(path);
        xOffset += w;
      });
    });

    // Group all teeth
    if (toothPaths.length > 0) {
      const group = new fabric.Group(toothPaths, {
        selectable: true,
        hasControls: true,
        customType: 'toothGroup',
        subTargetCheck: true,
      });
      fabricCanvas.add(group);
      fabricCanvas.setActiveObject(group);
    }

    fabricCanvas.renderAll();
    setSelectedTemplateId(template.id);
    saveState();
  }, [fabricCanvas, referencePoints, saveState]);

  // Save to Firestore
  const handleSave = useCallback(async () => {
    if (!fabricCanvas || !userProfile || !selectedPhoto) return;

    setSaving(true);
    try {
      const thumbnailDataURL = fabricCanvas.toDataURL({
        format: 'png',
        quality: 0.6,
        multiplier: 0.25,
      });

      // Serialize canvas without background image
      const canvasJSON = JSON.stringify(
        fabricCanvas.toJSON(['customType', 'toothType', 'toothVariant', 'side', 'toothIndex', 'pointKey', 'globalCompositeOperation'])
      );

      const designData = {
        name: `Smile Design - ${new Date().toLocaleDateString()}`,
        mode: activeMode,
        sourcePhoto: {
          phaseId: selectedPhase,
          photoId: selectedPhoto.id,
          downloadURL: selectedPhoto.downloadURL,
          category: selectedPhoto.category,
        },
        canvasJSON,
        referencePoints,
        designParams: {
          preset: designParams.preset,
          toothColor: designParams.toothColor,
          toothOpacity: designParams.toothOpacity,
          teethPerSide: designParams.teethPerSide,
          templateId: selectedTemplateId,
        },
        thumbnailDataURL,
      };

      if (selectedDesignId) {
        await updateSmileDesign(
          userProfile.practiceId, userProfile.id,
          patientId, caseId, selectedDesignId, designData
        );
      } else {
        const result = await saveSmileDesign(
          userProfile.practiceId, userProfile.id,
          patientId, caseId, designData
        );
        setSelectedDesignId(result.id);
      }

      const updatedDesigns = await getSmileDesigns(userProfile.practiceId, patientId, caseId);
      setDesigns(updatedDesigns);
    } catch (err) {
      console.error('Error saving design:', err);
      setError('Failed to save design');
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricCanvas, userProfile, selectedPhoto, activeMode, selectedPhase, referencePoints, designParams, selectedDesignId, selectedTemplateId, patientId, caseId]);

  // Export
  const handleExport = useCallback((format = 'png') => {
    if (!fabricCanvas) return;

    const dataURL = fabricCanvas.toDataURL({
      format,
      quality: format === 'jpeg' ? 0.95 : 1.0,
      multiplier: 2,
    });

    const link = document.createElement('a');
    link.download = `smile-design-${patientId}-${Date.now()}.${format}`;
    link.href = dataURL;
    link.click();

    if (userProfile) {
      logAudit({
        practiceId: userProfile.practiceId,
        userId: userProfile.id,
        action: AUDIT_ACTIONS.DOWNLOAD,
        resourceType: 'smileDesign',
        resourceId: selectedDesignId || 'unsaved',
        details: { format, patientId, caseId },
      });
    }
  }, [fabricCanvas, patientId, caseId, selectedDesignId, userProfile]);

  // AI Design generation
  const handleAiGenerate = useCallback(async (preset, customPrompt) => {
    if (!fabricCanvas || !selectedPhoto) return;

    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    setShowComparison(false);

    // Save the original photo URL before AI replaces the background
    setOriginalPhotoURL(selectedPhoto.downloadURL);

    try {
      const result = await generateAiSmileDesign({
        fabricCanvas,
        preset,
        customPrompt,
        practiceId: userProfile?.practiceId,
        userId: userProfile?.id,
        patientId,
        caseId,
      });

      setAiResult(result);
      if (result.generationsRemaining !== undefined) {
        setGenerationsRemaining(result.generationsRemaining);
      }

      // Load the AI result as the canvas background
      if (result.imageBase64) {
        const dataURL = `data:${result.mimeType};base64,${result.imageBase64}`;
        const img = await fabric.FabricImage.fromURL(dataURL, { crossOrigin: 'anonymous' });
        fitBackgroundImage(fabricCanvas, img);
        fabricCanvas.renderAll();
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setAiError(err.message || 'AI generation failed.');
    } finally {
      setAiLoading(false);
    }
  }, [fabricCanvas, selectedPhoto, userProfile, patientId, caseId]);

  // Load saved design
  const handleLoadDesign = useCallback(async (designId) => {
    if (!designId) {
      // "New Design" selected
      setSelectedDesignId(null);
      if (fabricCanvas) {
        const objects = fabricCanvas.getObjects().slice();
        objects.forEach(o => fabricCanvas.remove(o));
        fabricCanvas.renderAll();
      }
      setReferencePoints({ midline: null, leftCommissure: null, rightCommissure: null, incisalEdge: null });
      setDesignParams(prev => ({ ...prev, showTeeth: false }));
      clearHistory();
      return;
    }

    if (!userProfile) return;

    try {
      const design = await getSmileDesign(userProfile.practiceId, patientId, caseId, designId);

      // Set source photo
      if (design.sourcePhoto) {
        setSelectedPhase(design.sourcePhoto.phaseId);
        // Photo will auto-load from the phase effect
      }

      // Restore state
      if (design.referencePoints) setReferencePoints(design.referencePoints);
      if (design.designParams) {
        setDesignParams(prev => ({
          ...prev,
          ...design.designParams,
          showTeeth: design.mode === 'overlay',
        }));
      }
      if (design.mode) setActiveMode(design.mode);

      // Load canvas state after a short delay to let photo load
      if (fabricCanvas && design.canvasJSON) {
        setTimeout(() => {
          try {
            fabricCanvas.loadFromJSON(design.canvasJSON, () => {
              fabricCanvas.renderAll();
            });
          } catch (e) {
            console.error('Failed to load canvas JSON:', e);
          }
        }, 500);
      }

      setSelectedDesignId(designId);
      setSelectedTemplateId(design.designParams?.templateId || null);
      clearHistory();
    } catch (err) {
      console.error('Error loading design:', err);
      setError('Failed to load design');
    }
  }, [fabricCanvas, userProfile, patientId, caseId, clearHistory]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error && !patient) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <p className="text-red-600">{error}</p>
        <Link to={`/patients/${patientId}/cases/${caseId}`} className="text-blue-600 text-sm mt-2 inline-block">
          Back to Case
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-3">
        <Link to="/patients" className="hover:text-blue-600">Patients</Link>
        <span>/</span>
        <Link to={`/patients/${patientId}`} className="hover:text-blue-600">
          {patient ? `${patient.firstName} ${patient.lastName}` : '...'}
        </Link>
        <span>/</span>
        <Link to={`/patients/${patientId}/cases/${caseId}`} className="hover:text-blue-600">
          {treatmentCase?.name || 'Case'}
        </Link>
        <span>/</span>
        <span className="text-gray-800 font-medium">Smile Design</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          Smile Designer
        </h1>
        {error && (
          <p className="text-sm text-red-500">{error}</p>
        )}
      </div>

      {/* Hidden file input for direct photo upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUploadPhoto(file);
          e.target.value = '';
        }}
      />

      {/* Toolbar */}
      <SmileDesignerToolbar
        activeMode={activeMode}
        onModeChange={(mode) => {
          setActiveMode(mode);
          setActiveTool(null);
        }}
        onUndo={undo}
        onRedo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSave={handleSave}
        onExport={handleExport}
        saving={saving}
        selectedPhase={selectedPhase}
        phases={phases}
        onPhaseChange={setSelectedPhase}
        photos={photos}
        selectedPhoto={selectedPhoto}
        onPhotoChange={setSelectedPhoto}
        designs={designs}
        selectedDesignId={selectedDesignId}
        onDesignChange={handleLoadDesign}
        fabricCanvas={fabricCanvas}
        onUploadPhoto={triggerFileUpload}
      />

      {/* Main Content: Canvas + Sidebar */}
      <div className="flex gap-3">
        {/* Canvas */}
        <SmileDesignerCanvas
          fabricCanvas={fabricCanvas}
          setFabricCanvas={setFabricCanvas}
          selectedPhoto={selectedPhoto}
          activeMode={activeMode}
          activeTool={activeTool}
          referencePoints={referencePoints}
          onReferencePointChange={handleReferencePointChange}
          designParams={designParams}
          onSaveState={saveState}
          placingPoint={placingPoint}
          onPlacingPointDone={handlePlacingPointDone}
          onUploadPhoto={triggerFileUpload}
          aiLoading={aiLoading}
          aiResult={aiResult}
          showComparison={showComparison}
          originalPhotoURL={originalPhotoURL}
        />

        {/* Sidebar */}
        <SmileDesignerSidebar
          activeMode={activeMode}
          activeTool={activeTool}
          onToolChange={setActiveTool}
          referencePoints={referencePoints}
          placingPoint={placingPoint}
          onStartPlacing={handleStartPlacing}
          onClearPoint={handleClearPoint}
          designParams={designParams}
          onDesignParamsChange={setDesignParams}
          onApplyPreset={handleApplyPreset}
          onRegenerateTeeth={handleRegenerateTeeth}
          drawSettings={drawSettings}
          onDrawSettingsChange={setDrawSettings}
          selectedTemplateId={selectedTemplateId}
          onApplyTemplate={handleApplyTemplate}
          fabricCanvas={fabricCanvas}
          onSaveState={saveState}
          selectedPhoto={selectedPhoto}
          onAiGenerate={handleAiGenerate}
          aiResult={aiResult}
          aiLoading={aiLoading}
          aiError={aiError}
          showComparison={showComparison}
          onToggleComparison={() => setShowComparison(prev => !prev)}
          generationsRemaining={generationsRemaining}
        />
      </div>
    </div>
  );
};

export default SmileDesigner;
